import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {ObjectEntries, raise} from '@/utilities/helpers'
import {useTranslation} from '@/translations/i18n'
import {StyledTreatmentRequestScoreRow, StyledTreatmentRequestScoreRowLabel} from './style'
import {Controller, useFormContext, useWatch} from 'react-hook-form'
import {TreatmentRequestScore, TreatmentRequestScoresValidationSchema} from '@/features/treatment-requests/types'
import {RadioSteps} from '@/components/commons/radio-steps/RadioSteps'
import {PatientAgeRange} from '@/types'
import {useTreatmentRequestScores} from '@/features/treatment-requests/hooks/useTreatmentRequestScores'

export const TreatmentRequestScoresForm = ({
    serviceType
}: {
    serviceType: Exclude<PatientAgeRange, 'evolutionary_age_adults'>
}) => {
    const {t} = useTranslation()

    // methods
    const form = useFormContext<TreatmentRequestScoresValidationSchema>()
    const scores = useWatch({control: form.control, name: 'scores'})
    const diseases = useWatch({control: form.control, name: 'diseases'})

    const {scoresMap} = useTreatmentRequestScores({
        serviceType,
        diseases
    })

    return (
        <Flexbox direction="column" gap={4}>
            {ObjectEntries(scoresMap)
                .filter(([scoreType]) => {
                    if (scoreType === 'subtype') {
                        return diseases?.some(disease => disease.name === 'I° Gruppo')
                    }
                    return true
                })
                .map(([scoreType, scoreData]) => {
                    const scoreLabel = scoreData.label
                    const scoresData = scoreData.scores

                    return scoreLabel && scoresData ? (
                        <StyledTreatmentRequestScoreRow key={scoreType}>
                            <StyledTreatmentRequestScoreRowLabel>{t(scoreLabel)}</StyledTreatmentRequestScoreRowLabel>
                            <Controller
                                control={form.control}
                                name="scores"
                                render={({field}) => (
                                    <RadioSteps<number>
                                        name={scoreType}
                                        steps={scoresData}
                                        size="sm"
                                        inputType="checkbox"
                                        value={scores.find(score => score.type === scoreType)?.score ?? undefined}
                                        onChange={value => {
                                            const newScore = value
                                            const existingScore = scores.find(score => score.type === scoreType)
                                            if (existingScore && existingScore.score === newScore) {
                                                // Remove the score if clicking on the same radio button
                                                const updatedScores = scores.filter(score => score.type !== scoreType)
                                                field.onChange(updatedScores)
                                            } else {
                                                // Update or add the score
                                                const updatedScores = existingScore
                                                    ? scores.map(score =>
                                                          score.type === scoreType
                                                              ? {type: score.type, score: newScore}
                                                              : score
                                                      )
                                                    : scores.concat({
                                                          type: scoreType as TreatmentRequestScore['type'],
                                                          score: newScore
                                                      })
                                                field.onChange(updatedScores)
                                            }
                                        }}
                                    />
                                )}
                            />
                        </StyledTreatmentRequestScoreRow>
                    ) : (
                        raise('scoreLabel or scoreData are undefined')
                    )
                })}
        </Flexbox>
    )
}
